import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { AppDispatch, RootState } from '../store/store';
import { fetchMissingOverview, sendReminders } from '../slices/overviewSlice';

const MissingOverviewPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const {
    adminRows,
    adminLoading,
    adminError,
    reminderResult,
    reminderSending,
    reminderError,
  } = useSelector((state: RootState) => state.overview);

  useEffect(() => {
    dispatch(fetchMissingOverview());
  }, [dispatch]);

  const withoutPlanning = adminRows.filter((r) => !r.hasPlanning).length;

  const handleSendReminders = () => {
    if (
      !window.confirm(
        'Erinnerungs-Mails an alle Mitarbeiter ohne Monatsplanung auslösen?\n' +
          '(Es werden nur echte Mails versendet, wenn der Server nicht im Test-Modus läuft.)'
      )
    ) {
      return;
    }
    dispatch(sendReminders());
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h3 className="mb-0">Monatsplanung – Übersicht</h3>
        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={() => dispatch(fetchMissingOverview())}
            disabled={adminLoading}
          >
            Aktualisieren
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSendReminders}
            disabled={reminderSending || withoutPlanning === 0}
            title={withoutPlanning === 0 ? 'Alle Mitarbeiter haben eine Planung' : undefined}
          >
            {reminderSending ? 'Sende…' : 'Erinnerungen senden'}
          </button>
        </div>
      </div>

      <p className="text-muted">
        Monatsplanung der Mitarbeiter für den laufenden Monat.
        {withoutPlanning > 0
          ? ` ${withoutPlanning} Mitarbeiter ohne Planung.`
          : ' Alle Mitarbeiter haben ihre Planung eingetragen.'}
      </p>

      {adminError && <div className="alert alert-danger">{adminError}</div>}
      {reminderError && <div className="alert alert-danger">{reminderError}</div>}

      {reminderResult && (
        <div className={`alert ${reminderResult.dryRun ? 'alert-info' : 'alert-success'}`}>
          {reminderResult.dryRun ? (
            <>
              <strong>Test-Modus (dry-run):</strong> keine echten Mails gesendet.{' '}
              {reminderResult.processed} Mitarbeiter würden benachrichtigt
              {reminderResult.testEmail ? ` (Umleitung an ${reminderResult.testEmail})` : ''}.
            </>
          ) : (
            <>
              <strong>{reminderResult.results.filter((r) => r.sent).length}</strong> Erinnerungs-Mail(s) gesendet
              {reminderResult.testEmail ? ` (Umleitung an ${reminderResult.testEmail})` : ''}.
            </>
          )}
          {reminderResult.results.length > 0 && (
            <ul className="mb-0 mt-2">
              {reminderResult.results.map((r) => (
                <li key={r.user}>
                  {r.user} → {r.recipient || 'keine E-Mail'}{' '}
                  {r.error ? `❌ ${r.error}` : r.sent ? '✅' : reminderResult.dryRun ? '(Test)' : '⏸️'}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th>Mitarbeiter</th>
              <th className="text-center">Planung</th>
              <th className="text-center">Geplante Tage</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {adminLoading && adminRows.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-muted">Lädt…</td>
              </tr>
            ) : (
              adminRows.map((row) => (
                <tr
                  key={row.userId}
                  style={{ cursor: 'pointer' }}
                  title={`Kalender von ${row.username} öffnen`}
                  onClick={() => navigate(`/timesheet?userId=${row.userId}`)}
                >
                  <td>
                    <div className="fw-semibold">{row.username}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>{row.email}</div>
                  </td>
                  <td className="text-center">
                    {row.hasPlanning ? (
                      <span className="badge bg-success rounded-pill">Eingetragen</span>
                    ) : (
                      <span className="badge bg-danger rounded-pill">Fehlt</span>
                    )}
                  </td>
                  <td className="text-center">
                    <span className="text-muted">{row.plannedDaysCount}</span>
                  </td>
                  <td className="text-end text-primary">Kalender öffnen ›</td>
                </tr>
              ))
            )}
            {!adminLoading && adminRows.length === 0 && (
              <tr>
                <td colSpan={4} className="text-muted">Keine Mitarbeiter gefunden.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MissingOverviewPage;
